import type { AxiosError } from "axios";
import clone from "clone";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import type { NavigateFunction } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import type { ImmutableObject } from "@/lib/immutable";
import { storefrontSdk } from "@/storefrontsdk";
import type { Store } from "./store";
import { createStore, useStore } from "./store";
import { type AppConfigState, useAppConfigState } from "./useAppConfigState";
import type { Catalog } from "./useCatalogState";
import { type LocaleState, useLocaleState } from "./useLocaleState";
import { type SessionState, useSessionState } from "./useSessionState";
import {
	type ShoppingCartItemModel,
	type ShoppingCartItemsState,
	useShoppingCartItemsState,
} from "./useShoppingCartItemsState";
import { type SiteLayoutState, useSiteLayoutState } from "./useSiteLayoutState";
import {
	type StorefrontSettingsState,
	useStorefrontSettingsState,
} from "./useStorefrontSettingsState";
import type { TranslateFunction } from "./useTranslate/useTranslate";
import { useTranslate } from "./useTranslate/useTranslate";

// the portion of CheckoutFormModel that is stored in local storage
export class CheckoutFormLsModel {
	fromDateValue: string = dayjs().startOf("day").format("YYYY-MM-DD");
	fromTimeValue: string = "00:00";
	toDateValue: string = dayjs()
		.startOf("day")
		.add(1, "day")
		.subtract(1, "second")
		.format("YYYY-MM-DD");
	toTimeValue: string = "23:59";
	locationIdValue: string = "";
	warehouseIdValue: string = "";
	dealIdValue: string = "";
	quoteDescription: string = "";
	jobName: string = "";
	notesValue: string = "";
	fromDateByCatalogId: { [key: string]: string } = {};
	fromTimeByCatalogId: { [key: string]: string } = {};
	toDateByCatalogId: { [key: string]: string } = {};
	toTimeByCatalogId: { [key: string]: string } = {};
}

export class Deal {
	DealId: string = "";
	Deal: string = "";
}

export class CheckoutFormModel extends CheckoutFormLsModel {
	isValid: boolean = false;
	hasSubmitError: boolean = false;
	submitError: string = "";
	isSubmitted: boolean = false;
	isSubmitting: boolean = false;

	fromDateCaption: string = "From Date";
	fromDateHasError: boolean = false;
	fromDateError: string = "";
	fromDateRequiredError: string = "From Date is required";

	toDateCaption: string = "To Date";
	toDateHasError: boolean = false;
	toDateError: string = "";
	toDateRequiredError: string = "Stop Date is required";

	isLoadingDeals = false;
	isLoadingDealsFailed = false;
	deals?: Deal[];

	isLoadingLocation: boolean = false;
	lookuplocation?: WarehouseLocation[];

	isLoadingWarehouse: boolean = false;
	lookupwarehouse?: Warehouse[];

	notesCaption: string = "Notes";

	itemsValue?: ShoppingCartItemModel[];

	progressMeterSessionId?: string;
	progressMeterOpen: boolean = false;
}

const storefront_checkoutform_key = "storefront-checkoutform";
let initialLsState = new CheckoutFormLsModel();
const initialState = new CheckoutFormModel();
const localstorageJson = window.localStorage.getItem(
	storefront_checkoutform_key,
);
if (localstorageJson) {
	try {
		const today = dayjs().startOf("day");
		initialLsState = JSON.parse(localstorageJson);
		let updateLsState = false;
		if (dayjs(initialLsState.fromDateValue).isBefore(today)) {
			initialLsState.fromDateValue = today.format("YYYY-MM-DD");
			initialLsState.toDateValue = dayjs(initialLsState.fromDateValue)
				.add(1, "day")
				.format("YYYY-MM-DD");
			updateLsState = true;
		}
		if (
			dayjs(initialLsState.toDateValue).isBefore(initialLsState.fromDateValue)
		) {
			initialLsState.toDateValue = dayjs(initialLsState.fromDateValue)
				.add(1, "day")
				.format("YYYY-MM-DD");
			updateLsState = true;
		}
		if (updateLsState) {
			window.localStorage.setItem(
				storefront_checkoutform_key,
				JSON.stringify(initialLsState),
			);
		}
	} catch (ex) {
		console.error(ex);
	}
} else {
	window.localStorage.setItem(
		storefront_checkoutform_key,
		JSON.stringify(initialLsState),
	);
}
Object.assign(initialState, initialLsState);
const checkoutFormGlobalStore = createStore<CheckoutFormModel>(initialState);

export class ApiQuoteRequest {
	WebCatalogId?: string;
	Language?: "en" | "es" | "fw" | string;
	DepartmentId: string = "";
	LocationId: string = "";
	WarehouseId: string = "";
	DealId?: string;
	QuoteDescription?: string;

	JobName?: string;
	StartDate: string = "";
	StartTime?: string;
	StopDate: string = "";
	StopTime?: string;
	Notes?: string;
	Items: ApiQuoteRequestItem[] = [];
}

export class ApiQuoteRequestItem {
	InventoryId: string = "";
	//CatalogProductId?: string;
	WebCatalogId: string = "";
	Qty: number = 0;
	PackageItems?: ApiQuoteRequestPackageItem[];
}

export class ApiQuoteRequestPackageItem {
	Lineage?: string; // the parent packageid
	InventoryId: string = "";
	InventoryPackageInventoryId: string = "";
	Qty: number = 0;
}

export class CheckoutFormState {
	private appConfigState: AppConfigState;
	private localeState: LocaleState;
	private sessionState: SessionState;
	private checkoutFormState: Store<CheckoutFormModel>;
	private shoppingCartItemsState: ShoppingCartItemsState;
	private navigate: NavigateFunction;
	private siteLayoutState: SiteLayoutState;
	private storefrontSettingsState: StorefrontSettingsState;
	private translate: TranslateFunction;

	get(): ImmutableObject<CheckoutFormModel> {
		return this.checkoutFormState.getState();
	}

	constructor(
		appConfigState: AppConfigState,
		localeState: LocaleState,
		sessionState: SessionState,
		checkoutFormState: Store<CheckoutFormModel>,
		shoppingCartItemsState: ShoppingCartItemsState,
		navigate: NavigateFunction,
		siteLayoutState: SiteLayoutState,
		storefrontSettingsState: StorefrontSettingsState,
		translate: TranslateFunction,
	) {
		this.appConfigState = appConfigState;
		this.localeState = localeState;
		this.sessionState = sessionState;
		this.checkoutFormState = checkoutFormState;
		this.shoppingCartItemsState = shoppingCartItemsState;
		this.navigate = navigate;
		this.siteLayoutState = siteLayoutState;
		this.storefrontSettingsState = storefrontSettingsState;
		this.translate = translate;
	}

	private setStateValue<K extends keyof CheckoutFormModel>(
		key: K,
		value: CheckoutFormModel[K],
	) {
		this.checkoutFormState.setState((prev) => ({ ...prev, [key]: value }));
	}

	private setState(
		update:
			| Partial<CheckoutFormModel>
			| ((prev: CheckoutFormModel) => CheckoutFormModel),
	) {
		if (typeof update === "function") {
			this.checkoutFormState.setState(
				update as (prev: CheckoutFormModel) => CheckoutFormModel,
			);
			return;
		}
		this.checkoutFormState.setState((prev) => ({ ...prev, ...update }));
	}

	private saveToLocalStorage() {
		const values = this.checkoutFormState.getState();
		const schema = new CheckoutFormLsModel();

		// shallow copy only the keys defined in schema from the properties in values, so we can extract only the
		const obj = Object.assign(
			schema,
			// biome-ignore lint/suspicious/noExplicitAny: the key is typed as string, so it doesn't match on schema's properties
			...Object.keys(schema).map((key) => ({ [key]: (schema as any)[key] })),
			// biome-ignore lint/suspicious/noExplicitAny: the key is typed as string, so it doesn't match on schema's properties
			...Object.keys(schema).map((key) => ({ [key]: (values as any)[key] })),
		);
		let json = "";
		if (obj) {
			json = JSON.stringify(obj);
		}
		window.localStorage.setItem(storefront_checkoutform_key, json);
	}

	reset(catalogId?: string) {
		const whitelist = ["dealIdValue", "warehouseIdValue", "locationIdValue"];
		const checkoutFormLsJson = window.localStorage.getItem(
			storefront_checkoutform_key,
		);
		let cleared = false;
		// try to clear all but white-listed properties
		if (checkoutFormLsJson) {
			try {
				const checkoutFormLs = JSON.parse(
					checkoutFormLsJson,
				) as CheckoutFormLsModel;
				if (checkoutFormLs) {
					for (const key in checkoutFormLs) {
						const whiteListed = whitelist.indexOf(key) !== -1;
						if (!whiteListed) {
							delete checkoutFormLs[key as keyof CheckoutFormLsModel];
						}
					}

					// default the dates
					const today = dayjs().startOf("day");
					checkoutFormLs.fromDateValue = today.format("YYYY-MM-DD");
					checkoutFormLs.fromTimeValue = today.format("HH:mm");
					checkoutFormLs.toDateValue = dayjs(checkoutFormLs.fromDateValue)
						.add(
							this.storefrontSettingsState.get().settings
								?.ToDateAdjustmentDays ?? 0,
							"day",
						)
						.format("YYYY-MM-DD");
					checkoutFormLs.toTimeValue = today.format("HH:mm");
					window.localStorage.setItem(
						storefront_checkoutform_key,
						JSON.stringify(checkoutFormLs),
					);

					this.setStateValue("fromDateError", "");
					this.setStateValue("fromDateHasError", false);
					this.setStateValue("fromDateRequiredError", "");
					this.setStateValue("fromDateValue", checkoutFormLs.fromDateValue);
					this.setStateValue("fromTimeValue", checkoutFormLs.fromTimeValue);
					const fromDateByCatalogId =
						this.checkoutFormState.getState().fromDateByCatalogId;
					if (catalogId && fromDateByCatalogId[catalogId]) {
						const nextFromDates = { ...fromDateByCatalogId };
						delete nextFromDates[catalogId];
						this.setStateValue("fromDateByCatalogId", nextFromDates);
					} else {
						this.setStateValue("fromDateByCatalogId", {});
					}

					this.setStateValue("toDateError", "");
					this.setStateValue("toDateHasError", false);
					this.setStateValue("toDateRequiredError", "");
					this.setStateValue("toDateValue", checkoutFormLs.toDateValue);
					this.setStateValue("toTimeValue", checkoutFormLs.toTimeValue);
					const toDateByCatalogId =
						this.checkoutFormState.getState().toDateByCatalogId;
					if (catalogId && fromDateByCatalogId[catalogId]) {
						const nextToDates = { ...toDateByCatalogId };
						delete nextToDates[catalogId];
						this.setStateValue("toDateByCatalogId", nextToDates);
					} else {
						this.setStateValue("toDateByCatalogId", {});
					}
					cleared = true;
				}
			} catch {}
		}
		// if the above strategy didn't work then just clear the whole item
		if (!cleared) {
			window.localStorage.removeItem(storefront_checkoutform_key);
		}

		this.setStateValue("quoteDescription", "");
		this.setStateValue("jobName", "");
		this.setStateValue("notesValue", "");

		this.setStateValue("isValid", false);
		this.setStateValue("hasSubmitError", false);
		this.setStateValue("submitError", "");
		this.setStateValue("isSubmitted", false);
		this.setStateValue("isSubmitting", false);
	}

	// from date
	getFromDate(catalogid?: string): string {
		let result = this.checkoutFormState.getState().fromDateValue;
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		if (catalogid && separateQuoteRequestsByCatalog) {
			const fromDateByCatalogIds =
				this.checkoutFormState.getState().fromDateByCatalogId;
			const fromDateByCatalogId = fromDateByCatalogIds[catalogid];
			if (fromDateByCatalogId !== undefined) {
				result = fromDateByCatalogId;
			}
		}
		return result;
	}

	setFromDate(value: string, catalogid?: string) {
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		this.setStateValue("fromDateValue", value);
		//this.saveToLocalStorage();
		//this.validateStartDate();
		if (separateQuoteRequestsByCatalog && catalogid) {
			this.setState((prev) => ({
				...prev,
				fromDateByCatalogId: {
					...prev.fromDateByCatalogId,
					[catalogid]: value,
				},
			}));
			//this.saveToLocalStorage();
		}
		this.saveToLocalStorage();
	}

	// update the from date when switching catalogs
	updateFromDate(catalogid: string) {
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		if (separateQuoteRequestsByCatalog) {
			// update from date
			const fromDateByCatalogIds =
				this.checkoutFormState.getState().fromDateByCatalogId;
			const fromDateByCatalogId = fromDateByCatalogIds[catalogid];
			let fromDate = this.checkoutFormState.getState().fromDateValue;
			if (fromDateByCatalogId !== undefined) {
				fromDate = fromDateByCatalogId;
			} else {
				this.setState((prev) => ({
					...prev,
					fromDateByCatalogId: {
						...prev.fromDateByCatalogId,
						[catalogid]: fromDate,
					},
				}));
			}
			this.setStateValue("fromDateValue", fromDate);

			// update from time
			const fromTimeByCatalogIds =
				this.checkoutFormState.getState().fromTimeByCatalogId;
			const fromTimeByCatalogId = fromTimeByCatalogIds[catalogid];
			const fromTime = this.checkoutFormState.getState().fromTimeValue;
			if (fromTimeByCatalogId !== undefined) {
				fromDate = fromTimeByCatalogId;
			} else {
				this.setState((prev) => ({
					...prev,
					fromTimeByCatalogId: {
						...prev.fromTimeByCatalogId,
						[catalogid]: fromTime,
					},
				}));
			}
			this.setStateValue("fromTimeValue", fromTime);

			this.saveToLocalStorage();
		}
	}

	// from time
	getFromTime(catalogid?: string): string {
		let result = this.checkoutFormState.getState().fromTimeValue;
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		if (catalogid && separateQuoteRequestsByCatalog) {
			const fromTimeByCatalogIds =
				this.checkoutFormState.getState().fromTimeByCatalogId;
			const fromTimeByCatalogId = fromTimeByCatalogIds[catalogid];
			if (fromTimeByCatalogId !== undefined) {
				result = fromTimeByCatalogId;
			}
		}
		return result;
	}

	setFromTime(value: string, catalogid?: string) {
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		this.setStateValue("fromTimeValue", value);
		//this.saveToLocalStorage();
		//this.validateStartDate();
		if (separateQuoteRequestsByCatalog && catalogid) {
			this.setState((prev) => ({
				...prev,
				fromTimeByCatalogId: {
					...prev.fromTimeByCatalogId,
					[catalogid]: value,
				},
			}));
			//this.saveToLocalStorage();
		}
		this.saveToLocalStorage();
	}

	// to date
	getToDate(catalogid?: string): string {
		let result = this.checkoutFormState.getState().toDateValue;
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		if (catalogid && separateQuoteRequestsByCatalog) {
			const toDateByCatalogIds =
				this.checkoutFormState.getState().toDateByCatalogId;
			const toDateByCatalogId = toDateByCatalogIds[catalogid];
			if (toDateByCatalogId !== undefined) {
				result = toDateByCatalogId;
			}
		}
		return result;
	}

	setToDate(value: string, catalogid?: string) {
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		this.setStateValue("toDateValue", value);
		//this.saveToLocalStorage();
		if (separateQuoteRequestsByCatalog && catalogid) {
			this.setState((prev) => ({
				...prev,
				toDateByCatalogId: {
					...prev.toDateByCatalogId,
					[catalogid]: value,
				},
			}));
			//this.saveToLocalStorage();
		}
		this.saveToLocalStorage();
	}

	// update the from date when switching catalogs
	updateToDate(catalogid: string) {
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		if (separateQuoteRequestsByCatalog) {
			// update to date
			const toDateByCatalogIds =
				this.checkoutFormState.getState().toDateByCatalogId;
			const toDateByCatalogId = toDateByCatalogIds[catalogid];
			let toDate = this.checkoutFormState.getState().toDateValue;
			if (toDateByCatalogId !== undefined) {
				toDate = toDateByCatalogId;
			} else {
				this.setState((prev) => ({
					...prev,
					toDateByCatalogId: {
						...prev.toDateByCatalogId,
						[catalogid]: toDate,
					},
				}));
			}
			this.setStateValue("toDateValue", toDate);

			// update to time
			const toTimeByCatalogIds =
				this.checkoutFormState.getState().toTimeByCatalogId;
			const toTimeByCatalogId = toTimeByCatalogIds[catalogid];
			let toTime = this.checkoutFormState.getState().toTimeValue;
			if (toTimeByCatalogId !== undefined) {
				toTime = toTimeByCatalogId;
			} else {
				this.setState((prev) => ({
					...prev,
					toTimeByCatalogId: {
						...prev.toTimeByCatalogId,
						[catalogid]: toTime,
					},
				}));
			}
			this.setStateValue("toTimeValue", toDate);

			this.saveToLocalStorage();
		}
	}

	// to time
	getToTime(catalogid?: string): string {
		let result = this.checkoutFormState.getState().toTimeValue;
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		if (catalogid && separateQuoteRequestsByCatalog) {
			const toTimeByCatalogIds =
				this.checkoutFormState.getState().toTimeByCatalogId;
			const toTimeByCatalogId = toTimeByCatalogIds[catalogid];
			if (toTimeByCatalogId !== undefined) {
				result = toTimeByCatalogId;
			}
		}
		return result;
	}

	setToTime(value: string, catalogid?: string) {
		const separateQuoteRequestsByCatalog =
			this.storefrontSettingsState.get().settings
				?.SeparateQuoteRequestsByCatalog ?? false;
		this.setStateValue("toTimeValue", value);
		if (separateQuoteRequestsByCatalog && catalogid) {
			this.setState((prev) => ({
				...prev,
				toTimeByCatalogId: {
					...prev.toTimeByCatalogId,
					[catalogid]: value,
				},
			}));
		}
		this.saveToLocalStorage();
	}

	setDates(fromDate: string, toDate: string) {
		this.setState({
			fromDateValue: fromDate,
			toDateValue: toDate,
		});
		this.saveToLocalStorage();
	}

	setLocationId(value: string) {
		this.setStateValue("locationIdValue", value);
		const locations = this.checkoutFormState.getState().lookuplocation;
		const warehouseLocation = locations?.find((l) => l.LocationId === value);
		if (warehouseLocation) {
			this.setWarehouseId(warehouseLocation.WarehouseId);
		} else if (locations && locations.length > 0 && !value) {
			this.setWarehouseId("");
		}
		this.saveToLocalStorage();
	}

	setWarehouseId(value: string) {
		this.setStateValue("warehouseIdValue", value);
		this.saveToLocalStorage();
	}

	selectCatalogLocation(
		catalog?:
			| Pick<Catalog, "LocationId" | "WarehouseId">
			| ImmutableObject<Pick<Catalog, "LocationId" | "WarehouseId">>,
	) {
		if (!catalog) {
			return;
		}

		if (catalog.LocationId) {
			this.setLocationId(catalog.LocationId);
			return;
		}

		if (catalog.WarehouseId) {
			this.setWarehouseId(catalog.WarehouseId);
			const warehouseLocation = this.checkoutFormState
				.getState()
				.lookuplocation?.find((location) => {
					return location.WarehouseId === catalog.WarehouseId;
				});
			if (warehouseLocation) {
				this.setLocationId(warehouseLocation.LocationId);
			}
		}
	}

	setNotes(value: string) {
		this.setStateValue("notesValue", value);
		this.saveToLocalStorage();
	}

	setSelectedDealId(value: string | undefined) {
		this.setStateValue("dealIdValue", value ?? "");
		this.saveToLocalStorage();
	}

	setQuoteDescription(value: string) {
		this.setStateValue("quoteDescription", value);
		this.saveToLocalStorage();
	}

	setJobName(value: string) {
		this.setStateValue("jobName", value);
		this.saveToLocalStorage();
	}

	setProgressMeterSessionId(value: string | undefined) {
		this.setStateValue("progressMeterSessionId", value);
	}

	setProgressMeterOpen(value: boolean) {
		this.setStateValue("progressMeterOpen", value);
	}

	getWarehouse(): Warehouse | undefined {
		let location: WarehouseLocation | undefined;
		const warehouseId = this.checkoutFormState.getState().warehouseIdValue;
		const locations = this.checkoutFormState.getState().lookuplocation;
		//const isLoadingWarehouses = this.checkoutFormState.getState().isLoadingWarehouse;
		if (warehouseId !== undefined && locations !== undefined) {
			location = locations.find((l) => l.WarehouseId === warehouseId);
		}
		if (location) {
			const warehouse: Warehouse = {
				WarehouseId: location.WarehouseId,
				Warehouse: location.Warehouse,
			};
			return warehouse;
		}
		return undefined;
	}

	getLocation(): WarehouseLocation | undefined {
		let location: WarehouseLocation | undefined;
		const locationId = this.checkoutFormState.getState().locationIdValue;
		const locations = this.checkoutFormState.getState().lookuplocation;
		//const isLoadingWarehouses = this.checkoutFormState.getState().isLoadingWarehouse;
		if (locationId !== undefined && locations !== undefined) {
			location = locations.find((w) => w.LocationId === locationId);
		}
		return location;
	}

	async loadDealsAsync() {
		try {
			if (
				this.sessionState.get().loggedIn &&
				this.appConfigState.get().appConfig &&
				!this.checkoutFormState.getState().deals &&
				!this.checkoutFormState.getState().isLoadingDeals
			) {
				this.setStateValue("isLoadingDeals", true);
				const getDealsResponse = await storefrontSdk.getDealAsync();
				//console.log(getDealsResponse);
				const deals = getDealsResponse.data as Deal[];
				this.setStateValue("deals", deals);
				if (deals && deals.length > 0) {
					const selectedDealId = this.checkoutFormState.getState().dealIdValue;
					let selectedDeal: Deal | undefined;
					if (selectedDealId !== undefined) {
						selectedDeal = deals.find((o) => o.DealId === selectedDealId);
					}
					if (!selectedDeal) {
						this.setSelectedDealId(deals[0].DealId);
					}
				} else {
					this.setSelectedDealId(undefined);
				}
			}
			// biome-ignore lint/suspicious/noExplicitAny: any is the type of errors
		} catch (error: any) {
			//console.log(error);
			if (
				error?.response &&
				(error.response.status === 401 || error.response.status === 403)
			) {
				//enqueueSnackbar(`Your session has expired.`, { variant: "error" });
				this.siteLayoutState.redirectToLogin();
				return;
			}
			this.setStateValue("isLoadingDealsFailed", true);
			this.siteLayoutState.showError(error);
		} finally {
			this.setStateValue("isLoadingDeals", false);
		}
	}

	validateStartDate(newState?: Partial<CheckoutFormModel>) {
		let startDateHasError = false;
		let startDateError: string = "";
		if (
			!startDateHasError &&
			!this.checkoutFormState.getState().fromDateValue
		) {
			startDateHasError = true;
			startDateError = this.checkoutFormState.getState().fromDateRequiredError;
		}
		if (startDateHasError) {
			const updateState: boolean = !newState;
			if (!newState) {
				newState = newState ?? {};
			}
			newState.fromDateHasError = startDateHasError;
			newState.fromDateError = startDateError;
			if (updateState) {
				this.setState(newState);
			}
		}
	}

	validateStopDate(newState?: Partial<CheckoutFormModel>) {
		let stopDateHasError = false;
		let stopDateError: string = "";
		if (!stopDateHasError && !this.checkoutFormState.getState().toDateValue) {
			stopDateHasError = true;
			stopDateError = this.checkoutFormState.getState().toDateRequiredError;
		}
		if (stopDateHasError) {
			const updateState: boolean = !newState;
			if (!newState) {
				newState = newState ?? {};
			}
			newState.toDateHasError = stopDateHasError;
			newState.toDateError = stopDateError;
			if (updateState) {
				this.setState(newState);
			}
		}
	}

	validateForm(): void {
		// validate the login form, triggering a mutation to the newLoginState only if it needs to change
		const newState: Partial<CheckoutFormModel> = {};
		this.validateStartDate(newState);
		this.validateStopDate(newState);

		// update isValid and showValidationError states
		newState.isValid = !newState.fromDateHasError && !newState.toDateHasError;
		//newState.showValidationError = newState.isValid;

		// update the login form state
		if (Object.keys(newState).length > 0) {
			this.setState(newState);
		}
	}

	//getIsoDate(date: string): string {
	//    var d = new Date(date);
	//    const isoDateStr: string = [
	//        d.getFullYear(),
	//        ('0' + (d.getMonth() + 1)).slice(-2),
	//        ('0' + d.getDate()).slice(-2)
	//    ].join('-');
	//    return isoDateStr;
	//}

	async submitQuoteRequestAsync(catalogId?: string): Promise<void> {
		try {
			const fromDate = this.getFromDate(catalogId);
			const fromTime = this.getFromTime(catalogId);
			const toDate = this.getToDate(catalogId);
			const toTime = this.getToTime(catalogId);

			this.setStateValue("isSubmitting", true);
			// copy the state objects so we can use them in the web request
			const checkoutFormStateCopy = clone(
				this.checkoutFormState.getState(),
			) as CheckoutFormModel;
			const shoppingCartItemsCopy = clone(
				this.shoppingCartItemsState.get(),
			) as ShoppingCartItemModel[];

			const quoteRequest = new ApiQuoteRequest();
			quoteRequest.WebCatalogId = catalogId;
			quoteRequest.Language = this.localeState.get().language;
			quoteRequest.DepartmentId =
				shoppingCartItemsCopy[0].product.InventoryDepartmentId ?? "";
			quoteRequest.LocationId = checkoutFormStateCopy.locationIdValue;
			quoteRequest.WarehouseId = checkoutFormStateCopy.warehouseIdValue;
			if (checkoutFormStateCopy.dealIdValue === "-") {
				quoteRequest.DealId = "";
			} else {
				quoteRequest.DealId = checkoutFormStateCopy.dealIdValue;
			}
			quoteRequest.QuoteDescription = checkoutFormStateCopy.quoteDescription;
			quoteRequest.JobName = checkoutFormStateCopy.jobName;
			quoteRequest.StartDate = fromDate;
			quoteRequest.StartTime = fromTime;
			quoteRequest.StopDate = toDate;
			quoteRequest.StopTime = toTime;
			quoteRequest.Notes = checkoutFormStateCopy.notesValue;
			quoteRequest.Items = [];
			for (let i = 0; i < shoppingCartItemsCopy.length; i++) {
				const shoppingCartItem = shoppingCartItemsCopy[i];
				if (
					catalogId === undefined ||
					shoppingCartItem.product.WebCatalogId === catalogId
				) {
					const quoteRequestItem: ApiQuoteRequestItem = {
						InventoryId: shoppingCartItem.product.InventoryId,
						WebCatalogId: shoppingCartItem.product.WebCatalogId || "",
						Qty: shoppingCartItem.qty,
					};
					for (const key in shoppingCartItem.packageItemQtys) {
						if (shoppingCartItem.product.PackageItems) {
							const shoppingCartPackageItem =
								shoppingCartItem.product.PackageItems.find(
									(pi) => pi.InventoryId === key,
								);
							if (shoppingCartPackageItem) {
								const shoppingCartPackageItemQty =
									shoppingCartItem.packageItemQtys[key];
								const quoteRequestPackageItem: ApiQuoteRequestPackageItem = {
									Lineage: shoppingCartItem.product.Id,
									InventoryId: shoppingCartPackageItem.InventoryId,
									InventoryPackageInventoryId:
										shoppingCartPackageItem.InventoryPackageInventoryId,
									Qty: shoppingCartPackageItemQty,
								};
								if (!quoteRequestItem.PackageItems) {
									quoteRequestItem.PackageItems = [];
								}
								quoteRequestItem.PackageItems.push(quoteRequestPackageItem);
							}
						}
					}
					quoteRequest.Items.push(quoteRequestItem);
				}
			}
			//console.log('quoteRequest:', quoteRequest);
			try {
				const quoteRequestResponse =
					await storefrontSdk.submitQuoteRequestAsync(quoteRequest);
				const submitResponse =
					quoteRequestResponse.data as QuoteRequestResponse;
				if (submitResponse.Success && submitResponse.ProgressMeterSessionId) {
					this.setProgressMeterSessionId(submitResponse.ProgressMeterSessionId);
					this.setProgressMeterOpen(true);
				} else {
					this.setStateValue("hasSubmitError", true);
					this.setStateValue("submitError", "Invalid credentials.");
				}
				// biome-ignore lint/suspicious/noExplicitAny: any is ok here
			} catch (error: any) {
				if ((error as AxiosError)?.response?.status === 400) {
					this.setStateValue("hasSubmitError", true);
					this.setStateValue(
						"submitError",
						"Bad request. Make sure sure form is valid.",
					);
					this.siteLayoutState.showError(error);
				} else if ((error as AxiosError)?.response?.status === 401) {
					this.siteLayoutState.redirectToLogin();
				} else if ((error as AxiosError)?.response?.status === 403) {
					this.siteLayoutState.redirectToLogin();
				} else if ((error as AxiosError)?.response?.status === 500) {
					this.setStateValue("hasSubmitError", true);
					this.setStateValue("submitError", "An an occured on the server.");
				} else {
					this.siteLayoutState.showError(error);
				}
			}

			//if (quoteRequestResponse.status === 200) {
			//    if (quoteRequestResponse.data.Success && quoteRequestResponse.data.ProgressMeterSessionId) {
			//        this.setProgressMeterSessionId(quoteRequestResponse.data.ProgressMeterSessionId);
			//        this.setProgressMeterOpen(true);

			//        // need to do this elsewhere after progress meter is complete
			//        //this.setStateValue('isSubmitted', true);
			//        //this.setStateValue('hasSubmitError', false);
			//        //this.setStateValue('submitError', '');
			//        //this.shoppingCartItemsState.reset();
			//        //enqueueSnackbar(this.translate(`Quote request submitted`), {
			//        //    variant: 'success'
			//        //});
			//        //this.reset();
			//        //this.navigate('/');
			//        //this.reset();
			//    }
			//    else {
			//        this.setStateValue('hasSubmitError', true);
			//        this.setStateValue('submitError', 'Invalid credentials.');
			//    }
			//}
			//if (quoteRequestResponse.status === 400) {
			//    this.setStateValue('hasSubmitError', true);
			//    this.setStateValue('submitError', 'Bad request. Make sure sure form is valid.');
			//}
			//else if (quoteRequestResponse.status === 401) {
			//    this.siteLayoutState.logoutAsync();
			//    this.siteLayoutState.setLoginFormPopupVisible(true);
			//}
			//else if (quoteRequestResponse.status === 403) {
			//    this.siteLayoutState.logoutAsync();
			//    this.siteLayoutState.setLoginFormPopupVisible(true);
			//}
			//else if (quoteRequestResponse.status === 500) {
			//    this.setStateValue('hasSubmitError', true);
			//    this.setStateValue('submitError', 'An an occured on the server.');
			//}
			//console.log('quoteRequestResponse:', quoteRequestResponse);
		} catch (error) {
			this.siteLayoutState.showError(error);
		} finally {
			this.setStateValue("isSubmitting", false);
		}
	}

	async loadLocationsAsync(): Promise<void> {
		try {
			if (
				this.appConfigState.get().appConfig &&
				!this.checkoutFormState.getState().isLoadingLocation
			) {
				this.setStateValue("isLoadingLocation", true);
				const lookuplocationResponse = await storefrontSdk.getLocationsAsync();
				//const locationid = this.quoteRequestFormState.get().locationIdValue;
				this.setStateValue(
					"lookuplocation",
					lookuplocationResponse.data as WarehouseLocation[],
				);
				//if (lookuplocationResponse.data.length > 0) {
				//    if (!locationid) {
				//        this.setLocationId(lookuplocationResponse.data[0].LocationId);
				//    }
				//}
			}
		} finally {
			this.setStateValue("isLoadingLocation", false);
		}
	}

	async loadWarehousesAsync(): Promise<void> {
		try {
			if (
				this.appConfigState.get().appConfig &&
				!this.checkoutFormState.getState().isLoadingWarehouse &&
				!this.checkoutFormState.getState().lookupwarehouse
			) {
				this.setStateValue("isLoadingWarehouse", true);
				const lookupwarehouseResponse =
					await storefrontSdk.getWarehousesAsync();
				const warehouses = lookupwarehouseResponse.data as Warehouse[];
				this.setStateValue("lookupwarehouse", warehouses);
				//const warehouseid = this.quoteRequestFormState.get().warehouseIdValue;

				this.setStateValue("lookupwarehouse", warehouses);
				//if (lookupwarehouseResponse.data.length > 0) {
				//    if (!warehouseid) {
				//        this.setWarehouseId(lookupwarehouseResponse.data[0].WarehouseId)
				//    }
				//}
			}
		} finally {
			this.setStateValue("isLoadingWarehouse", false);
		}
	}

	async updateDatesAsync(): Promise<void> {}

	getDurationInDays(): number {
		let fromDate: dayjs.Dayjs | undefined, toDate: dayjs.Dayjs | undefined;
		if (this.checkoutFormState.getState().fromDateValue) {
			fromDate = dayjs(this.checkoutFormState.getState().fromDateValue);
		}
		if (this.checkoutFormState.getState().toDateValue) {
			toDate = dayjs(this.checkoutFormState.getState().toDateValue);
		}
		let durationDays = 0;
		if (fromDate !== undefined && toDate !== undefined) {
			durationDays = toDate.diff(fromDate, "day") + 1;
		}
		return durationDays;
	}

	setSubmitComplete(catalogId?: string): void {
		this.setStateValue("progressMeterSessionId", undefined);
		this.setStateValue("isSubmitted", true);
		this.setStateValue("hasSubmitError", false);
		this.setStateValue("submitError", "");
		this.shoppingCartItemsState.reset(
			this.get().locationIdValue,
			this.get().warehouseIdValue,
			catalogId,
		);
		enqueueSnackbar(this.translate(`Submitted`), {
			variant: "success",
		});
		this.reset(catalogId);
		this.navigate("/");
		//this.reset();
	}

	setSubmitFailed(): void {
		this.setStateValue("progressMeterSessionId", undefined);
	}

	isFormDisabled(): boolean {
		return (
			this.checkoutFormState.getState().isSubmitted ||
			this.checkoutFormState.getState().isSubmitting
		);
	}
}

export type WarehouseLocation = {
	Id: string; // locationid~warehouseid
	LocationId: string;
	Location: string;
	WarehouseId: string;
	Warehouse: string;
};

export type Warehouse = {
	WarehouseId: string;
	Warehouse: string;
};

type QuoteRequestResponse = {
	Success: boolean;
	FailureMessage: string;
	ProgressMeterSessionId: string;
};

export function useCheckoutFormState(): CheckoutFormState {
	const appConfigState = useAppConfigState();
	const localeState = useLocaleState();
	const sessionState = useSessionState();
	const shoppingCartItemsState = useShoppingCartItemsState();
	const navigate = useNavigate();
	const siteLayoutState = useSiteLayoutState();
	const storefrontSettingsState = useStorefrontSettingsState();
	const translate = useTranslate();
	useStore(checkoutFormGlobalStore);
	return useMemo(
		() =>
			new CheckoutFormState(
				appConfigState,
				localeState,
				sessionState,
				checkoutFormGlobalStore,
				shoppingCartItemsState,
				navigate,
				siteLayoutState,
				storefrontSettingsState,
				translate,
			),
		[
			appConfigState,
			localeState,
			sessionState,
			shoppingCartItemsState,
			navigate,
			siteLayoutState,
			storefrontSettingsState,
			translate,
		],
	);
}
